/**
 * Booking Service
 *
 * Frontend service for managing practitioner availability,
 * booking settings, and public booking API calls.
 */

import { api } from './api';

// ========================================
// TYPES
// ========================================

export interface WeeklySchedule {
  id?: string;
  day_of_week: number; // 0-6 (Sunday-Saturday)
  start_time: string; // HH:MM:SS
  end_time: string;
  is_available: boolean;
  timezone?: string;
}

export interface ScheduleException {
  id: string;
  practitioner_id: string;
  exception_date: string; // YYYY-MM-DD
  exception_type: 'holiday' | 'time_off' | 'custom_hours' | 'blocked';
  is_all_day: boolean;
  start_time?: string;
  end_time?: string;
  is_available: boolean;
  notes?: string;
  range_group_id?: string;
  created_at?: string;
}

export interface BookingSettings {
  id?: string;
  practitioner_id?: string;
  require_credit_card: boolean;
  auto_accept_appointments: boolean;
  buffer_time_minutes: number;
  advance_booking_min_hours: number;
  advance_booking_max_days: number;
  max_bookings_per_day?: number;
  allow_patient_cancellation: boolean;
  cancellation_notice_hours: number;
  cancellation_fee_cents?: number;
  no_show_fee_cents?: number;
  booking_link_active: boolean;
  booking_page_message?: string;
  confirmation_email_enabled: boolean;
  reminder_email_enabled: boolean;
  reminder_sms_enabled: boolean;
  timezone?: string;
}

export interface AppointmentType {
  id: string;
  name: string;
  description?: string;
  duration_minutes: number;
  price?: string;
  price_cents?: number;
  color?: string;
  telehealth_enabled: boolean;
  in_person_enabled: boolean;
  is_active: boolean;
  is_public: boolean;
  // Optional default pre-visit intake config
  default_intake_form_template_id?: string | null;
  default_therapy_category?: string | null;
}

export interface TimeSlot {
  start: string; // ISO datetime
  end: string;
  local_start?: string;
  local_end?: string;
}

export interface AvailableDate {
  date: string; // YYYY-MM-DD
  hasSlots: boolean;
}

export interface BookingConfirmation {
  appointment: {
    id: string;
    start_time: string;
    end_time: string;
    status: string;
    is_telehealth: boolean;
    telehealth_link?: string;
  };
  practitioner: {
    name: string;
    credentials?: string;
  };
  message: string;
}

export interface CreateExceptionRequest {
  exception_date: string;
  exception_type: 'time_off' | 'custom_hours';
  is_all_day?: boolean;
  start_time?: string;
  end_time?: string;
  notes?: string;
}

export interface CreateDateRangeExceptionRequest {
  start_date: string;
  end_date: string;
  notes?: string;
}

export interface DateRangeExceptionResponse {
  range_group_id: string;
  created_count: number;
  skipped_count: number;
  skipped_dates: string[];
}

export interface PatientFormData {
  first_name: string;
  middle_name?: string;
  last_name: string;
  gender: string;
  dob: string;
  species?: string;
  animal_owner_name?: string;
  weight?: string;
  weight_recorded_date?: string;
  height?: number;
  medication_allergies?: string[];
  no_known_allergies?: boolean;
  medical_history?: string;
  preferred_contact_methods?: {
    email?: boolean;
    sms?: boolean;
    phone?: boolean;
  };
  emergency_contact_phone?: string;
  contacts?: Array<{ key: string; value: string }>;
  addresses?: Array<{
    addressLine1?: string;
    addressLine2?: string;
    addressLine3?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  }>;
}

// ========================================
// PRIVATE (AUTHENTICATED) BOOKING SERVICE
// ========================================

export const bookingService = {
  // ========================================
  // Availability Schedule
  // ========================================

  /**
   * Get weekly availability schedule
   */
  async getAvailability(clinicId: string): Promise<WeeklySchedule[]> {
    const response = await api.get(`/clinic/${clinicId}/booking/availability`);
    return response.data || [];
  },

  /**
   * Update weekly availability schedule
   */
  async updateAvailability(
    clinicId: string,
    schedule: WeeklySchedule[],
    timezone?: string
  ): Promise<WeeklySchedule[]> {
    const response = await api.put(`/clinic/${clinicId}/booking/availability`, {
      schedule,
      timezone,
    });
    return response.data || [];
  },

  // ========================================
  // Booking Settings
  // ========================================

  /**
   * Get booking settings
   */
  async getSettings(clinicId: string): Promise<BookingSettings> {
    const response = await api.get(`/clinic/${clinicId}/booking/settings`);
    return response.data;
  },

  /**
   * Update booking settings
   */
  async updateSettings(
    clinicId: string,
    settings: Partial<BookingSettings>
  ): Promise<BookingSettings> {
    const response = await api.put(`/clinic/${clinicId}/booking/settings`, settings);
    return response.data;
  },

  // ========================================
  // Schedule Exceptions
  // ========================================

  /**
   * Get schedule exceptions
   */
  async getExceptions(
    clinicId: string,
    startDate?: string,
    endDate?: string
  ): Promise<ScheduleException[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const response = await api.get(`/clinic/${clinicId}/booking/exceptions?${params.toString()}`);
    return response.data || [];
  },

  /**
   * Create a schedule exception
   */
  async createException(
    clinicId: string,
    exception: CreateExceptionRequest
  ): Promise<ScheduleException> {
    const response = await api.post(`/clinic/${clinicId}/booking/exceptions`, exception);
    return response.data;
  },

  /**
   * Delete a schedule exception
   */
  async deleteException(clinicId: string, exceptionId: string): Promise<void> {
    await api.delete(`/clinic/${clinicId}/booking/exceptions/${exceptionId}`);
  },

  /**
   * Create schedule exceptions for a date range
   */
  async createDateRangeException(
    clinicId: string,
    request: CreateDateRangeExceptionRequest
  ): Promise<DateRangeExceptionResponse> {
    const response = await api.post(`/clinic/${clinicId}/booking/exceptions/range`, request);
    return response.data;
  },

  /**
   * Delete all exceptions in a range group
   */
  async deleteExceptionGroup(clinicId: string, groupId: string): Promise<void> {
    await api.delete(`/clinic/${clinicId}/booking/exceptions/range/${groupId}`);
  },

  // ========================================
  // Appointment Types
  // ========================================

  /**
   * Get appointment types
   */
  async getAppointmentTypes(clinicId: string, practitionerId?: string, active_only?: boolean): Promise<AppointmentType[]> {
    const params = new URLSearchParams();
    if (practitionerId) params.append('practitioner_id', practitionerId);
    if (active_only) params.append('active_only', active_only.toString());

    const response = await api.get(
      `/clinic/${clinicId}/booking/appointment-types?${params.toString()}`
    );
    return response.data || [];
  },

  /**
   * Create an appointment type
   */
  async createAppointmentType(
    clinicId: string,
    data: Partial<AppointmentType>
  ): Promise<AppointmentType> {
    const response = await api.post(`/clinic/${clinicId}/booking/appointment-types`, data);
    return response.data;
  },

  /**
   * Update an appointment type
   */
  async updateAppointmentType(
    clinicId: string,
    typeId: string,
    data: Partial<AppointmentType>
  ): Promise<AppointmentType> {
    const response = await api.put(`/clinic/${clinicId}/booking/appointment-types/${typeId}`, data);
    return response.data;
  },

  /**
   * Delete an appointment type
   */
  async deleteAppointmentType(clinicId: string, typeId: string): Promise<void> {
    await api.delete(`/clinic/${clinicId}/booking/appointment-types/${typeId}`);
  },

  // ========================================
  // Booking Links (V2)
  // ========================================

  /**
   * Get V2 (identity-first / OTP-verified) booking links for the clinic.
   * Returns one URL per active appointment type plus a default URL.
   */
  async getV2BookingLinks(clinicId: string): Promise<{
    clinic: { id: string; name: string; booking_slug: string };
    base_url: string;
    default_url: string | null;
    // Preselected-practitioner default (V2 URL with `?p=<slug>` appended).
    // Null when the logged-in caller isn't a practitioner.
    practitioner_default_url: string | null;
    current_practitioner_slug: string | null;
    appointment_types: Array<{
      id: string;
      name: string;
      duration_minutes: number;
      color?: string | null;
      telehealth_enabled: boolean;
      in_person_enabled: boolean;
      booking_slug: string;
      url: string;
      // Same as `url` with `?p=<practitioner-slug>` so V2 skips the
      // provider chooser. Null for non-practitioner callers.
      practitioner_url: string | null;
    }>;
  }> {
    const response = await api.get(`/clinic/${clinicId}/booking/v2-links`);
    return response.data;
  },

  /**
   * Regenerate the booking_slug for a single appointment type (invalidates
   * the previously-shared per-type V2 link).
   */
  async regenerateAppointmentTypeSlug(
    clinicId: string,
    typeId: string,
  ): Promise<{ booking_slug: string; url: string | null }> {
    const response = await api.post(
      `/clinic/${clinicId}/booking/appointment-types/${typeId}/regenerate-slug`,
    );
    return response.data;
  },
};

// ========================================
// PUBLIC BOOKING SERVICE — cancel (no auth required)
// ========================================

export const publicBookingService = {
  /**
   * Cancel a booking (email-verified, works for both V1 and V2 appointments).
   */
  async cancelBooking(
    appointmentId: string,
    email: string,
    reason?: string,
  ): Promise<{ message: string }> {
    const response = await api.post(`/public/book/cancel/${appointmentId}`, { email, reason });
    return response;
  },
};

// ========================================
// PUBLIC BOOKING SERVICE V2 — clinic+type-scoped, OTP-verified
// ========================================

export interface ClinicLandingPage {
  clinic: {
    id: string;
    name: string;
    city?: string;
    state?: string;
    booking_slug: string;
  };
  practitioner: {
    id: string;
    full_name: string;
    credentials?: string;
    specialization?: string;
    booking_slug: string;
  } | null;
  appointment_types: Array<{
    id: string;
    name: string;
    description?: string;
    duration_minutes: number;
    price?: string;
    price_cents?: number;
    color?: string;
    telehealth_enabled: boolean;
    in_person_enabled: boolean;
    booking_slug: string;
  }>;
}

export interface ClinicTypeBookingPage {
  clinic: {
    id: string;
    name: string;
    city?: string;
    state?: string;
    booking_slug: string;
    stripe_account_id?: string;
  };
  appointment_type: {
    id: string;
    name: string;
    description?: string;
    duration_minutes: number;
    price?: string;
    price_cents?: number;
    color?: string;
    telehealth_enabled: boolean;
    in_person_enabled: boolean;
    booking_slug: string;
  };
  practitioners: Array<{
    id: string;
    full_name: string;
    credentials?: string;
    specialization?: string;
    timezone: string;
    booking_slug: string;
  }>;
}

export interface RequestOtpRequest {
  email: string;
  clinic_id: string;
  appointment_type_id: string;
  practitioner_id: string;
  slot_start: string;
  slot_end: string;
  timezone?: string;
  booking_flow_id?: string;
}

export interface RequestOtpResponse {
  sent: boolean;
  /** Only present when the target was valid. Treat absence as "session not
   * usable" — but never surface that distinction to the patient. */
  session_ref?: string;
  expires_in_minutes: number;
}

export type PatientStatus = 'existing' | 'new' | 'ambiguous';

export interface VerifyOtpResponse {
  session_token: string;
  patient_status: PatientStatus;
  patient_preview: { first_name: string; last_initial: string } | null;
}

export interface OtpBookingConfirmation {
  appointment: {
    id: string;
    start_time: string;
    end_time: string;
    status: string;
    is_telehealth: boolean;
    telehealth_link?: string;
  };
  patient_id?: string;
  /**
   * Pre-visit intake — populated by the API when an intake was auto-attached
   * (via the appointment type's default_intake_form_template_id /
   * default_therapy_category, or a per-link override).
   */
  intake?: {
    intakeId: string;
    intakeLink: string;
    templateId?: string | null;
    therapyCategory?: string | null;
  } | null;
}

export const publicBookingV2Service = {
  async getClinicLandingPage(
    clinicSlug: string,
    practitionerSlug?: string | null,
  ): Promise<ClinicLandingPage> {
    const qs = practitionerSlug
      ? `?p=${encodeURIComponent(practitionerSlug)}`
      : '';
    const response = await api.get(`/public/book/clinic/${clinicSlug}${qs}`);
    return response.data;
  },

  async getClinicTypePage(
    clinicSlug: string,
    typeSlug: string,
    practitionerSlug?: string | null,
  ): Promise<ClinicTypeBookingPage> {
    const qs = practitionerSlug
      ? `?p=${encodeURIComponent(practitionerSlug)}`
      : '';
    const response = await api.get(`/public/book/clinic/${clinicSlug}/type/${typeSlug}${qs}`);
    return response.data;
  },

  async getDates(
    clinicSlug: string,
    typeSlug: string,
    practitionerId: string,
    year: number,
    month: number,
    visitorTimezone?: string,
  ): Promise<{ year: number; month: number; dates: AvailableDate[] }> {
    const params = new URLSearchParams({
      practitioner_id: practitionerId,
      year: year.toString(),
      month: month.toString(),
    });
    if (visitorTimezone) params.append('timezone', visitorTimezone);
    const response = await api.get(
      `/public/book/clinic/${clinicSlug}/type/${typeSlug}/dates?${params.toString()}`,
    );
    return response.data;
  },

  async getSlots(
    clinicSlug: string,
    typeSlug: string,
    practitionerId: string,
    date: string,
    visitorTimezone?: string,
  ): Promise<{
    slots: TimeSlot[];
    timezone: string;
    duration: number;
    date: string;
    reason?: string;
  }> {
    const params = new URLSearchParams({
      practitioner_id: practitionerId,
      date,
    });
    if (visitorTimezone) params.append('timezone', visitorTimezone);
    const response = await api.get(
      `/public/book/clinic/${clinicSlug}/type/${typeSlug}/slots?${params.toString()}`,
    );
    return response.data;
  },

  /**
   * Request an OTP for the chosen slot. ALWAYS treat the response as
   * "code sent" regardless of whether `session_ref` is present, so the
   * UI doesn't accidentally leak target validity.
   */
  async requestOtp(payload: RequestOtpRequest): Promise<RequestOtpResponse> {
    const response = await api.post('/public/book/session/request-otp', payload);
    return response.data;
  },

  async verifyOtp(
    sessionRef: string,
    otp: string,
    sourceToken?: string,
  ): Promise<VerifyOtpResponse> {
    const response = await api.post('/public/book/session/verify-otp', {
      session_ref: sessionRef,
      otp,
      // Proof-of-guided-intake: lets the backend widen the patient-match
      // scope to include cross-clinic grants via external_patients.
      ...(sourceToken ? { source_token: sourceToken } : {}),
    });
    return response.data;
  },

  async confirmDob(
    sessionToken: string,
    dob: string,
  ): Promise<{
    confirmed: boolean;
    patient_id: string;
    patient: { first_name: string; last_name: string };
  }> {
    const response = await api.post(
      `/public/book/session/${sessionToken}/confirm-dob`,
      { dob },
    );
    return response.data;
  },

  /**
   * Phase 3 split: onboard a new patient WITHOUT booking the slot.
   * Use this so a consent gate can run in between onboarding + the final
   * /confirm call.
   */
  async onboard(
    sessionToken: string,
    patientForm: PatientFormData,
  ): Promise<{
    patient_id: string;
    patient: { first_name: string; last_name: string };
  }> {
    const response = await api.post(
      `/public/book/session/${sessionToken}/onboard`,
      { patient_form: patientForm },
    );
    return response.data;
  },

  /**
   * Final booking step. Books the appointment for the patient_id bound
   * to the session by either /confirm-dob (existing) or /onboard (new).
   * The frontend should run any required consents BEFORE calling this.
   */
  async confirm(
    sessionToken: string,
    payload: { reason?: string; timezone?: string; is_telehealth?: boolean; payment_method_id?: string },
  ): Promise<OtpBookingConfirmation> {
    const response = await api.post(
      `/public/book/session/${sessionToken}/confirm`,
      payload,
    );
    return response.data;
  },

  async getPaymentInfo(sessionToken: string): Promise<BookingPaymentInfo> {
    const response = await api.get(`/public/book/session/${sessionToken}/payment-info`);
    return response.data;
  },

  async createSetupIntent(sessionToken: string): Promise<{ client_secret: string; stripe_account_id: string }> {
    const response = await api.post(`/public/book/session/${sessionToken}/setup-intent`);
    return response.data;
  },
};

export interface BookingPaymentInfo {
  requires_payment: boolean;
  price_cents: number;
  payment_methods: Array<{
    id: string;
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  }>;
  stripe_account_id: string;
}

export default bookingService;
